/**
 * The study's scoring fields, verbatim from the static page (`scoring()` in
 * apps/cli/src/page/render.ts) so both artifacts score identically. Every value
 * is a closed enum; nothing here can hold text a person typed.
 *
 * Not a study artifact: these are the judgments the correction sheet (M4.1)
 * will take, so study mode is the correction UI in embryo.
 */
import type { LedgerItem } from '@cordelia/core'

export type ScoreField =
  | 'evidence'
  | 'openness'
  | 'org_attribution'
  | 'modality_matches'
  | 'action'
  | 'correction_reason'
  | 'history'

export const SCORE_FIELDS: readonly ScoreField[] = [
  'evidence',
  'openness',
  'org_attribution',
  'modality_matches',
  'action',
  'correction_reason',
  'history',
]

export type ScoreOption = readonly [value: string, label: string]

export type FieldSpec = {
  readonly field: ScoreField
  readonly label: string
  readonly options: readonly ScoreOption[]
}

export const EVIDENCE: FieldSpec = {
  field: 'evidence',
  label: 'Did this person actually communicate this?',
  options: [
    ['yes', 'Yes'],
    ['no', 'No'],
  ],
}

export const OPENNESS: FieldSpec = {
  field: 'openness',
  label: 'Is it still open now?',
  options: [
    ['open', 'Open'],
    ['not_open', 'Not open'],
    ['uncertain', 'Uncertain'],
  ],
}

export const ORG_ATTRIBUTION: FieldSpec = {
  field: 'org_attribution',
  label: 'Organization, recipient and your relationship correct?',
  options: [
    ['correct', 'All correct'],
    ['wrong_org', 'Wrong organization'],
    ['wrong_recipient', 'Wrong recipient'],
    ['wrong_relationship', 'Wrong relationship'],
  ],
}

export const MODALITY_MATCHES: FieldSpec = {
  field: 'modality_matches',
  label: 'Force shown (will / try / asked) matches what was said?',
  options: [
    ['yes', 'Yes'],
    ['no', 'No'],
  ],
}

export const HISTORY: FieldSpec = {
  field: 'history',
  label: 'Was this done?',
  options: [
    ['done', 'Done'],
    ['still_open', 'Still open'],
    ['delegated', 'Someone else did it'],
    ['dropped', 'Dropped'],
  ],
}

export const ACTION: FieldSpec = {
  field: 'action',
  label: 'What did you do?',
  options: [
    ['none', 'Nothing'],
    ['reply', 'Replied'],
    ['act', 'Acted on it'],
    ['delegate', 'Delegated'],
    ['mark_done', 'Marked done'],
    ['mark_received', 'Marked received'],
    ['follow_up', 'Followed up'],
    ['dismiss', 'Dismissed'],
    ['correct', 'Corrected'],
  ],
}

export const CORRECTION_REASON: FieldSpec = {
  field: 'correction_reason',
  label: 'If corrected, why?',
  options: [
    ['not_a_commitment', 'Not a commitment'],
    ['request_not_accepted', 'Request was not accepted'],
    ['wrong_person', 'Wrong person'],
    ['org_owes_not_me', 'The organization owes this, not me'],
    ['already_done', 'Already done'],
    ['wrong_timing', 'Wrong timing'],
    ['irrelevant_or_private', 'Irrelevant or private'],
    ['draft_abandoned', 'Draft abandoned intentionally'],
  ],
}

export const ALL_SPECS: readonly FieldSpec[] = [
  EVIDENCE,
  OPENNESS,
  ORG_ATTRIBUTION,
  MODALITY_MATCHES,
  HISTORY,
  ACTION,
  CORRECTION_REASON,
]

/** The rows a card gets, in the page's order. */
export function fieldsFor(
  item: Pick<LedgerItem, 'promise_scope' | 'surface'>,
): readonly FieldSpec[] {
  const rows: FieldSpec[] = [EVIDENCE, OPENNESS]
  if (item.promise_scope === 'organizational' || item.promise_scope === 'collective')
    rows.push(ORG_ATTRIBUTION)
  rows.push(MODALITY_MATCHES)
  if (item.surface === 'history') rows.push(HISTORY)
  else rows.push(ACTION, CORRECTION_REASON)
  return rows
}

/** One card's judgments: enum values by field, plus when it was last touched. */
export type Judgment = Partial<Readonly<Record<ScoreField, string>>> & {
  readonly updated_at?: string
}
